import { useMutation } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import Nbsp from '@components/Nbsp';
import BodyCopy from '@components/typography/BodyCopy';
import Hyperlink from '@components/typography/Hyperlink';
import {
  AutomationActionBlockCreateDocument,
  AutomationActionBlockCreateMutation,
  AutomationActionBlockCreateMutationVariables,
  AutomationActionBlockKindEnum,
  AutomationBlockOutputEnum,
  AutomationConditionBlockCreateDocument,
  AutomationConditionBlockCreateMutation,
  AutomationConditionBlockCreateMutationVariables,
  AutomationTimingBlockCreateDocument,
  AutomationTimingBlockCreateMutation,
  AutomationTimingBlockCreateMutationVariables,
  AutomationFragment as AutomationType,
  DurationUnitsEnum
} from '@typing/Generated';

type ParentData = {
  blockId: string;
  blockOutput: AutomationBlockOutputEnum;
};

type Block = NonNullable<AutomationType['blocks'][number]>;

type Props = {
  automation: AutomationType;
  childBlock?: {
    __typename: string;
    id: string;
  };
  onBlockAdded: (block: Block) => void;
  parentBlockId?: string;
  parentBlockOutput?: AutomationBlockOutputEnum;
};

const AddParentBlock = ({ automation, childBlock, onBlockAdded, parentBlockId, parentBlockOutput }: Props) => {
  const parentData = useMemo<ParentData | undefined>(() => {
    if (parentBlockId && parentBlockOutput) {
      return {
        blockId: parentBlockId,
        blockOutput: parentBlockOutput
      };
    } else if (childBlock) {
      let result: ParentData | undefined = undefined;
      automation.blocks.forEach(block => {
        if (block.__typename === 'AutomationConditionBlock') {
          if (block.childBlockFalse?.id === childBlock.id) {
            result = { blockId: block.id, blockOutput: AutomationBlockOutputEnum.CONDITION_FALSE };
          }
          if (block.childBlockTrue?.id === childBlock.id) {
            result = { blockId: block.id, blockOutput: AutomationBlockOutputEnum.CONDITION_TRUE };
          }
        }
        if (block.__typename === 'AutomationActionBlock' && block.childBlock?.id === childBlock.id) {
          result = { blockId: block.id, blockOutput: AutomationBlockOutputEnum.ACTION };
        }
        if (block.__typename === 'AutomationTimingBlock' && block.childBlock?.id === childBlock.id) {
          result = { blockId: block.id, blockOutput: AutomationBlockOutputEnum.TIMING };
        }
        if (block.__typename === 'AutomationTriggerBlock' && block.childBlock?.id === childBlock.id) {
          result = { blockId: block.id, blockOutput: AutomationBlockOutputEnum.TRIGGER };
        }
      });
      return result;
    }
  }, [automation.blocks, childBlock, parentBlockId, parentBlockOutput]);

  const reportBlockAdded = useCallback(
    (automation: AutomationType | undefined | null, newBlockId: string | undefined | null) => {
      if (automation && newBlockId) {
        const newBlock = automation.blocks.find(block => block.id === newBlockId);
        if (newBlock) {
          onBlockAdded(newBlock);
        }
      }
    },
    [onBlockAdded]
  );

  const [createAutomationActionBlock] = useMutation<
    AutomationActionBlockCreateMutation,
    AutomationActionBlockCreateMutationVariables
  >(AutomationActionBlockCreateDocument);

  const [createAutomationConditionBlock] = useMutation<
    AutomationConditionBlockCreateMutation,
    AutomationConditionBlockCreateMutationVariables
  >(AutomationConditionBlockCreateDocument);

  const [createAutomationTimingBlock] = useMutation<
    AutomationTimingBlockCreateMutation,
    AutomationTimingBlockCreateMutationVariables
  >(AutomationTimingBlockCreateDocument);

  const handleAddActionBlock = useCallback(() => {
    if (parentData) {
      createAutomationActionBlock({
        variables: {
          automationId: automation.id,
          kind: AutomationActionBlockKindEnum.NO_OP,
          parentBlockId: parentData.blockId,
          parentBlockOutput: parentData.blockOutput
        }
      }).then(response => {
        reportBlockAdded(
          response.data?.automationActionBlockCreate?.automation,
          response.data?.automationActionBlockCreate?.automationActionBlock?.id
        );
      });
    }
  }, [automation.id, createAutomationActionBlock, parentData, reportBlockAdded]);

  const handleAddConditionBlock = useCallback(() => {
    if (parentData) {
      createAutomationConditionBlock({
        variables: {
          automationId: automation.id,
          parentBlockId: parentData.blockId,
          parentBlockOutput: parentData.blockOutput
        }
      }).then(response => {
        reportBlockAdded(
          response.data?.automationConditionBlockCreate?.automation,
          response.data?.automationConditionBlockCreate?.automationConditionBlock?.id
        );
      });
    }
  }, [automation.id, createAutomationConditionBlock, parentData, reportBlockAdded]);

  const handleAddTimingBlock = useCallback(() => {
    if (parentData) {
      createAutomationTimingBlock({
        variables: {
          automationId: automation.id,
          duration: 0,
          durationUnits: DurationUnitsEnum.MINUTES,
          parentBlockId: parentData.blockId,
          parentBlockOutput: parentData.blockOutput
        }
      }).then(response => {
        reportBlockAdded(
          response.data?.automationTimingBlockCreate?.automation,
          response.data?.automationTimingBlockCreate?.automationTimingBlock?.id
        );
      });
    }
  }, [automation.id, createAutomationTimingBlock, parentData, reportBlockAdded]);

  if (automation.locked) {
    return null;
  }

  return (
    <div>
      <BodyCopy>
        <FormattedMessage id="models.automation.addAParent" />
        <Nbsp />
        <Hyperlink onClick={handleAddActionBlock}>
          <FormattedMessage id="models.automation.action" />
        </Hyperlink>
        <FormattedMessage id="dictionary.punctuation.comma" />
        <Nbsp />
        <Hyperlink onClick={handleAddConditionBlock}>
          <FormattedMessage id="models.automation.condition" />
        </Hyperlink>
        <FormattedMessage id="dictionary.punctuation.comma" />
        <Nbsp />
        <FormattedMessage id="dictionary.or" />
        <Nbsp />
        <Hyperlink onClick={handleAddTimingBlock}>
          <FormattedMessage id="models.automation.timing" />
        </Hyperlink>
      </BodyCopy>
    </div>
  );
};

export default AddParentBlock;
